/**
 * Frame and navigation.
 *
 * The nav is built from the schema response, which the API has already filtered
 * to the config types the caller may read. So a user without debtor-consent
 * access never sees the link — and, more to the point, nobody has to remember
 * to add a link when a config type is created.
 *
 * The same is true one level up, for regions. The switcher below is rendered
 * from `regions` in that response, so someone who works in one region receives
 * one region and gets no switcher at all. Nothing here decides who may see
 * what — global-admin-only screens such as the mailbox estate are simply absent
 * from everyone else's schema.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useSchema } from '../api/hooks'
import type { ConfigTypeSchema } from '../api/types'
import { RegionProvider, readStoredRegion, writeStoredRegion } from '../region'

const UNGROUPED = 'Configuration'

export function AppShell({ children, onSignOut }: { children: ReactNode; onSignOut?: () => void }) {
  const { data } = useSchema()
  const navigate = useNavigate()
  const location = useLocation()

  const regions = useMemo(() => data?.regions ?? [], [data])
  const [preferred, setPreferred] = useState<string | null>(() => readStoredRegion())

  // On a config screen the route decides, because its key already carries the
  // region — deriving it means a deep link into the other region can never
  // leave the sidebar contradicting what is on screen, and no effect has to
  // chase the two into agreement.
  const routeType = location.pathname.startsWith('/config/')
    ? location.pathname.slice('/config/'.length)
    : null
  const routeRegion = data?.config_types.find((t) => t.key === routeType)?.region

  // Off a config screen the stored preference decides, then the person's own
  // region. The preference is a hint, not a fact: grants change, and a region
  // that is no longer visible has to fall back rather than blank the sidebar.
  const ownRegion = data?.user.region
  const region =
    routeRegion ??
    regions.find((r) => r.key === preferred)?.key ??
    regions.find((r) => r.key === ownRegion)?.key ??
    regions[0]?.key ??
    ''

  const visible = useMemo(
    () => (data?.config_types ?? []).filter((t) => t.region === region),
    [data, region],
  )

  const groups = useMemo(() => {
    const byGroup = new Map<string, ConfigTypeSchema[]>()
    for (const type of visible) {
      const key = type.group ?? UNGROUPED
      const existing = byGroup.get(key)
      if (existing) existing.push(type)
      else byGroup.set(key, [type])
    }
    return Array.from(byGroup.entries())
  }, [visible])

  const user = data?.user
  const canGrant = Boolean(user?.can_manage_access)
  const activeLabel = regions.find((r) => r.key === region)?.label

  const changeRegion = (next: string) => {
    setPreferred(next)
    writeStoredRegion(next)
    // Only a config screen has to move: its key is region-qualified, so staying
    // put would leave the other region's table open under the new region's nav.
    // Recent changes and Access re-scope themselves and should stay where they
    // are, because moving someone off the screen they chose is rude.
    if (routeType) {
      const first = data?.config_types.find((t) => t.region === next)
      navigate(first ? `/config/${first.key}` : '/')
    }
  }

  return (
    <RegionProvider value={{ region, setRegion: changeRegion }}>
      <div className="shell">
        <nav className="sidebar" aria-label="Configuration sections">
          <div className="sidebar__brand">
            <div className="sidebar__brand-title">Email configuration</div>
            <div className="sidebar__brand-sub">
              {activeLabel ? `${activeLabel} email automation` : 'Email automation'}
            </div>
          </div>

          {groups.map(([group, types]) => (
            <div key={group}>
              <div className="sidebar__group">{group}</div>
              {types.map((type) => (
                <NavLink
                  key={type.key}
                  to={`/config/${type.key}`}
                  className={({ isActive }) => `sidebar__link ${isActive ? 'is-active' : ''}`}
                >
                  <span>{type.label}</span>
                  {!type.permissions.write ? (
                    <span className="badge badge--off" title="Read-only">
                      view
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}

          {/* Admins only. General users see a row's own history in its edit
              panel; the API refuses them the feed and the access list anyway. */}
          {canGrant ? (
            <>
              <div className="sidebar__group">Platform</div>
              <NavLink
                to="/audit"
                className={({ isActive }) => `sidebar__link ${isActive ? 'is-active' : ''}`}
              >
                Recent changes
              </NavLink>
              <NavLink
                to="/access"
                className={({ isActive }) => `sidebar__link ${isActive ? 'is-active' : ''}`}
              >
                Access
              </NavLink>
            </>
          ) : null}

          <div className="sidebar__footer">
            {regions.length > 1 ? (
              <div className="region-switch">
                <label className="region-switch__label" htmlFor="region-switch">
                  Region
                </label>
                <select
                  id="region-switch"
                  className="select select--filter"
                  value={region}
                  onChange={(event) => changeRegion(event.target.value)}
                >
                  {regions.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {user ? (
              <>
                <div className="sidebar__user">{user.display_name}</div>
                <div>{user.is_global_admin ? 'Global admin' : user.role_label}</div>
              </>
            ) : null}
            {onSignOut ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={onSignOut}>
                Sign out
              </button>
            ) : null}
          </div>
        </nav>

        <main className="main">{children}</main>
      </div>
    </RegionProvider>
  )
}
